








































































































import { Component, Prop, Watch } from 'vue-property-decorator';
import _throttle from 'lodash/throttle';
import { SwExtendedVue } from '../sw-extended-vue';
import { domService } from '../dom-service';
import { CSSStyleOptions } from '../common-types';
import SwScrollViewScrollbar from './sw-scroll-view-scrollbar.vue';
import { OverflowEvent } from './types';

const cssPrefix = 'sw-scroll-view';

/**
 * @event content-dom-updated
 * @event scroll
 * @event overflow `(event: OverflowEvent)`
 */
@Component({
  components: {
    SwScrollViewScrollbar,
  },
})
export default class SwScrollView extends SwExtendedVue {
  @Prop({ type: Boolean, default: false })
  nativeScrollbar: boolean;

  @Prop({ type: String, default: 'light' })
  scrollbarTheme: 'light' | 'dark';

  @Prop({ type: Boolean, default: false })
  scrollbarAutoHide: boolean;

  @Prop({ type: Number, default: 1 })
  scrollbarZIndex: number;

  private cssPrefix = cssPrefix;

  public clientWidth: number = 0;
  public clientHeight: number = 0;
  public scrollWidth: number = 0;
  public scrollHeight: number = 0;
  public scrollTop: number = 0;
  public scrollLeft: number = 0;
  public lockScrollbar: boolean = false;
  private hideScrollbar: boolean = false;
  private contentObserver: MutationObserver | null = null;

  get viewStyle(): CSSStyleOptions {
    if (this.nativeScrollbar) {
      return {};
    }
    return {
      marginRight: `${-1 * domService.getScrollbarSize()}px`,
      marginBottom: `${-1 * domService.getScrollbarSize()}px`,
    };
  }
  get possibleShowVScrollbar() {
    return this.scrollHeight > this.clientHeight;
  }
  get possibleShowHScrollbar() {
    return this.scrollWidth > this.clientWidth;
  }
  get showVScrollbar(): boolean {
    return this.possibleShowVScrollbar && (!this.effectiveHideScrollbar);
  }
  get showHScrollbar(): boolean {
    return this.possibleShowHScrollbar && (!this.effectiveHideScrollbar);
  }
  get effectiveHideScrollbar(): boolean {
    return (!this.lockScrollbar && this.hideScrollbar);
  }

  @Watch('possibleShowVScrollbar')
  private onYOverflowChange() {
    this.$emit('overflow', { axis: 'y', overflow: this.possibleShowVScrollbar } as OverflowEvent);
  }
  @Watch('possibleShowHScrollbar')
  private onXOverflowChange() {
    this.$emit('overflow', { axis: 'x', overflow: this.possibleShowHScrollbar } as OverflowEvent);
  }

  private syncSizes() {
    const viewContEl: HTMLElement | undefined = this.$refs.viewCont as HTMLElement;
    if (!viewContEl) { return Promise.resolve(false); }
    return new Promise<boolean>((resolve) => {
      window.requestAnimationFrame(() => {
        this.clientWidth = viewContEl.clientWidth;
        this.clientHeight = viewContEl.clientHeight;
        this.scrollWidth = viewContEl.scrollWidth;
        this.scrollHeight = viewContEl.scrollHeight;
        this.scrollTop = viewContEl.scrollTop;
        this.scrollLeft = viewContEl.scrollLeft;
        resolve(true);
      });
    });
  }

  public updateSizes() {
    if (!this.$refs.viewCont) { return Promise.resolve(); }
    return this.syncSizes()
      .then(() => {
        this.updateScrollbarPercentage();
      });
  }

  private updateScrollbarPercentage(): void {
    if (!this.$refs.vScroll && !this.$refs.hScroll) { return; }

    if (this.$refs.vScroll && this.showVScrollbar) {
      (this.$refs.vScroll as SwScrollViewScrollbar).updatePosition(this.scrollTop / (this.scrollHeight - this.clientHeight));
    }
    if (this.$refs.hScroll && this.showHScrollbar) {
      (this.$refs.hScroll as SwScrollViewScrollbar).updatePosition(this.scrollLeft / (this.scrollWidth - this.clientWidth));
    }
  }

  public updatePercentagePosition(percentagePos: number, forVertical: boolean) {
    if (!this.$refs.viewCont) { return; }
    const viewContEl = this.$refs.viewCont as HTMLElement;
    if (forVertical) {
      viewContEl.scrollTop = percentagePos * (viewContEl.scrollHeight - viewContEl.clientHeight);
    } else {
      viewContEl.scrollLeft = percentagePos * (viewContEl.scrollWidth - viewContEl.clientWidth);
    }
  }

  /** @public imperatively set top/left */
  public updatePosition(pos: number, forVertical: boolean) {
    if (!this.$refs.viewCont) { return; }
    const viewContEl = this.$refs.viewCont as HTMLElement;
    if (forVertical) {
      viewContEl.scrollTop = pos;
      this.scrollTop = viewContEl.scrollTop;
    } else {
      viewContEl.scrollLeft = pos;
      this.scrollLeft = viewContEl.scrollLeft;
    }
  }

  private onViewScroll(event: Event) {
    this.updateSizes()
      .then(() => {
        this.$emit('scroll', event);
      });
  }

  private onMouseEnterView() {
    this.hideScrollbar = false;
  }

  private onMouseLeaveView() {
    this.hideScrollbar = this.scrollbarAutoHide;
  }

  private onContentDomUpdated() {
    this.updateSizes()
      .then(() => {
        this.$emit('content-dom-updated');
      });
  }

  private get throttledContentDomUpdated() {
    return _throttle(this.onContentDomUpdated, 500);
  }

  private onContentTransitionEnd() {
    this.throttledContentDomUpdated();
  }

  private mounted() {
    this.$nextTick(() => {
      this.updateSizes()
        .then(() => {
          this.onYOverflowChange();
          this.onXOverflowChange();
        });
      (this.$refs.viewCont as HTMLElement).addEventListener('scroll', this.onViewScroll);
      domService.resizeObserver.on(this.$refs.slotWrapper as HTMLElement, this.updateSizes);
      domService.resizeObserver.on(this.$el as HTMLElement, this.updateSizes);
      domService.visibilityObserver.on(this.$el as HTMLElement, this.updateSizes);

      /** Since it is hard to make a generic sensor for content width, we are using MutationObserver
       * as an alternative way to monitor the size change of contained content.
       */
      this.contentObserver = domService.mutationObserverManager.setup(
        this.$refs.slotWrapper as HTMLElement,
        { childList: true, subtree: true, attributes: true, characterData: true },
        this.throttledContentDomUpdated,
      );
      (this.$refs.slotWrapper as HTMLElement).addEventListener('transitionend', this.onContentTransitionEnd);

      if (this.scrollbarAutoHide) {
        this.$nextTick(() => {
          this.hideScrollbar = true;
          this.$el.addEventListener('mouseenter', this.onMouseEnterView);
          this.$el.addEventListener('mouseleave', this.onMouseLeaveView);
        });
      }
    });
  }

  private beforeDestroy() {
    if (this.$refs.viewCont) {
      (this.$refs.viewCont as HTMLElement).removeEventListener('scroll', this.onViewScroll);
    }
    if (this.$el) {
      this.$el.removeEventListener('mouseenter', this.onMouseEnterView);
      this.$el.removeEventListener('mouseleave', this.onMouseLeaveView);
      domService.resizeObserver.off(this.$el as HTMLElement, this.updateSizes);
      domService.visibilityObserver.off(this.$el as HTMLElement);
    }
    if (this.$refs.slotWrapper) {
      domService.resizeObserver.off(this.$refs.slotWrapper as HTMLElement, this.updateSizes);
      (this.$refs.slotWrapper as HTMLElement).removeEventListener('transitionend', this.onContentTransitionEnd);
    }
    if (this.contentObserver) {
      this.contentObserver.disconnect();
    }
  }
}
